import {
	CodeBlock,
	Endpoint,
	Alert,
	ParamList,
	Param,
	Table,
} from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function ApiAdmin() {
	return (
		<div className={styles.docsPage}>
			<h1>Admin API Reference</h1>
			<p className={styles.lead}>
				The Admin API provides platform administrators with full control over
				operators, transactions, and analytics. These endpoints are restricted
				to authenticated admin users with appropriate permissions.
			</p>

			<Alert variant="error" title="Admin Access Only">
				These endpoints require admin authentication. They are not accessible to
				operators or players. Unauthorized access attempts are logged and may
				result in account suspension.
			</Alert>

			{/* Authentication */}
			<h2>Authentication</h2>
			<p>
				Admin API endpoints require a valid admin JWT token obtained through the
				login endpoint. Tokens expire after 24 hours and must be refreshed.
			</p>

			<CodeBlock language="http" title="Authorization Header">
				{`Authorization: Bearer <adminToken>`}
			</CodeBlock>

			{/* Operators */}
			<h2>Operators</h2>
			<p>
				Manage operator accounts, including creating new operators, updating
				settings, and managing API keys.
			</p>

			{/* List Operators */}
			<h3 id="list-operators">List Operators</h3>
			<Endpoint
				method="GET"
				path="/api/v1/admin/operators"
				description="Retrieves a paginated list of all operators on the platform with their current status and summary statistics."
			/>

			<h4>Query Parameters</h4>
			<ParamList>
				<Param name="status" type="string">
					Filter by status: <code>active</code>, <code>suspended</code>,{' '}
					<code>pending</code>.
				</Param>
				<Param name="search" type="string">
					Search operators by name or email.
				</Param>
				<Param name="sortBy" type="string">
					Sort field: <code>name</code>, <code>createdAt</code>,{' '}
					<code>totalGgr</code>, <code>activePlayers</code>. Default:{' '}
					<code>createdAt</code>.
				</Param>
				<Param name="sortOrder" type="string">
					Sort direction: <code>asc</code> or <code>desc</code>. Default:{' '}
					<code>desc</code>.
				</Param>
				<Param name="limit" type="number">
					Results per page (1-100, default: 20).
				</Param>
				<Param name="offset" type="number">
					Number of results to skip for pagination.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X GET "https://api.rngfort.com/api/v1/admin/operators?status=active&limit=10" \\
  -H "Authorization: Bearer <adminToken>"`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "operators": [
      {
        "id": "op_abc123",
        "name": "Lucky Casino",
        "email": "admin@luckycasino.com",
        "status": "active",
        "walletMode": "seamless",
        "createdAt": "2024-01-01T00:00:00Z",
        "stats": {
          "totalPlayers": 1250,
          "activePlayers": 342,
          "totalBets": 125000,
          "totalWagered": "12500000",
          "totalGgr": "625000",
          "lastActivityAt": "2024-01-15T14:30:00Z"
        },
        "apiKeys": {
          "test": 2,
          "live": 1
        }
      },
      {
        "id": "op_def456",
        "name": "Crypto Bet",
        "email": "support@cryptobet.io",
        "status": "active",
        "walletMode": "integrated",
        "createdAt": "2024-01-05T12:00:00Z",
        "stats": {
          "totalPlayers": 890,
          "activePlayers": 156,
          "totalBets": 78000,
          "totalWagered": "7800000",
          "totalGgr": "390000",
          "lastActivityAt": "2024-01-15T13:45:00Z"
        },
        "apiKeys": {
          "test": 1,
          "live": 1
        }
      }
    ],
    "pagination": {
      "total": 12,
      "limit": 10,
      "offset": 0,
      "hasMore": true
    }
  }
}`}
			</CodeBlock>

			{/* Create Operator */}
			<h3 id="create-operator">Create Operator</h3>
			<Endpoint
				method="POST"
				path="/api/v1/admin/operators"
				description="Creates a new operator account and generates initial API credentials."
			/>

			<h4>Request Body</h4>
			<ParamList>
				<Param name="name" type="string" required>
					Display name for the operator (2-100 characters).
				</Param>
				<Param name="email" type="string" required>
					Contact email address. Must be unique across the platform.
				</Param>
				<Param name="walletMode" type="string" required>
					Default wallet integration mode: <code>seamless</code> or{' '}
					<code>integrated</code>.
				</Param>
				<Param name="webhookUrl" type="string">
					URL for webhook notifications. Required if <code>walletMode</code> is{' '}
					<code>seamless</code>.
				</Param>
				<Param name="allowedCurrencies" type="string[]">
					List of allowed currency codes. Default: all currencies enabled.
				</Param>
				<Param name="allowedGames" type="string[]">
					List of allowed games. Default: all games enabled.
				</Param>
				<Param name="revenueShare" type="number">
					Revenue share percentage (0-100). Platform takes this percentage of
					GGR.
				</Param>
				<Param name="metadata" type="object">
					Custom metadata for internal tracking.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X POST https://api.rngfort.com/api/v1/admin/operators \\
  -H "Authorization: Bearer <adminToken>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "New Gaming Site",
    "email": "admin@newgaming.com",
    "walletMode": "seamless",
    "webhookUrl": "https://newgaming.com/api/ringfort/wallet",
    "allowedCurrencies": ["USD", "EUR", "BTC"],
    "allowedGames": ["dice", "plinko", "coinflip"],
    "revenueShare": 15,
    "metadata": {
      "salesRep": "john_smith",
      "tier": "premium"
    }
  }'`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="201 Created">
				{`{
  "success": true,
  "data": {
    "operator": {
      "id": "op_ghi789",
      "name": "New Gaming Site",
      "email": "admin@newgaming.com",
      "status": "active",
      "walletMode": "seamless",
      "webhookUrl": "https://newgaming.com/api/ringfort/wallet",
      "allowedCurrencies": ["USD", "EUR", "BTC"],
      "allowedGames": ["dice", "plinko", "coinflip"],
      "revenueShare": 15,
      "createdAt": "2024-01-15T15:00:00Z"
    },
    "credentials": {
      "testKeys": {
        "publicKey": "pk_test_abc123xyz",
        "secretKey": "sk_test_ONLY_SHOWN_ONCE_def456uvw"
      },
      "liveKeys": {
        "publicKey": "pk_live_ghi789rst",
        "secretKey": "sk_live_ONLY_SHOWN_ONCE_jkl012mno"
      },
      "webhookSecret": "whsec_ONLY_SHOWN_ONCE_pqr345stu"
    }
  }
}`}
			</CodeBlock>

			<Alert variant="warning" title="Store Credentials Securely">
				The secret keys and webhook secret are only shown once at creation time.
				Store them securely. If lost, new keys must be generated.
			</Alert>

			{/* Get Operator Details */}
			<h3 id="get-operator">Get Operator Details</h3>
			<Endpoint
				method="GET"
				path="/api/v1/admin/operators/:id"
				description="Retrieves detailed information about a specific operator including configuration, API keys, and comprehensive statistics."
			/>

			<h4>Path Parameters</h4>
			<ParamList>
				<Param name="id" type="string" required>
					The unique operator identifier.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X GET https://api.rngfort.com/api/v1/admin/operators/op_abc123 \\
  -H "Authorization: Bearer <adminToken>"`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "operator": {
      "id": "op_abc123",
      "name": "Lucky Casino",
      "email": "admin@luckycasino.com",
      "status": "active",
      "walletMode": "seamless",
      "webhookUrl": "https://luckycasino.com/api/wallet",
      "allowedCurrencies": ["USD", "EUR", "BTC", "ETH"],
      "allowedGames": ["dice", "plinko", "coinflip", "crash"],
      "revenueShare": 10,
      "createdAt": "2024-01-01T00:00:00Z",
      "updatedAt": "2024-01-10T12:00:00Z",
      "metadata": {
        "salesRep": "jane_doe",
        "tier": "enterprise"
      }
    },
    "apiKeys": [
      {
        "id": "key_001",
        "name": "Production Key",
        "publicKey": "pk_live_abc123...",
        "environment": "live",
        "lastUsedAt": "2024-01-15T14:30:00Z",
        "createdAt": "2024-01-01T00:00:00Z"
      },
      {
        "id": "key_002",
        "name": "Development Key",
        "publicKey": "pk_test_def456...",
        "environment": "test",
        "lastUsedAt": "2024-01-14T10:00:00Z",
        "createdAt": "2024-01-01T00:00:00Z"
      }
    ],
    "stats": {
      "lifetime": {
        "totalPlayers": 1250,
        "totalBets": 125000,
        "totalWagered": "12500000",
        "totalWon": "11875000",
        "totalGgr": "625000",
        "platformRevenue": "62500"
      },
      "thisMonth": {
        "totalPlayers": 342,
        "totalBets": 15234,
        "totalWagered": "1523400",
        "totalGgr": "76170"
      },
      "byGame": {
        "dice": { "bets": 60000, "ggr": "300000" },
        "plinko": { "bets": 35000, "ggr": "175000" },
        "coinflip": { "bets": 20000, "ggr": "100000" },
        "crash": { "bets": 10000, "ggr": "50000" }
      }
    },
    "webhookHealth": {
      "status": "healthy",
      "lastSuccessAt": "2024-01-15T14:29:55Z",
      "successRate": 99.8,
      "averageLatency": 145
    }
  }
}`}
			</CodeBlock>

			{/* Update Operator */}
			<h3 id="update-operator">Update Operator</h3>
			<Endpoint
				method="PUT"
				path="/api/v1/admin/operators/:id"
				description="Updates an operator's configuration. Only provided fields are updated."
			/>

			<h4>Path Parameters</h4>
			<ParamList>
				<Param name="id" type="string" required>
					The unique operator identifier.
				</Param>
			</ParamList>

			<h4>Request Body</h4>
			<ParamList>
				<Param name="name" type="string">
					Updated display name.
				</Param>
				<Param name="email" type="string">
					Updated contact email.
				</Param>
				<Param name="status" type="string">
					Operator status: <code>active</code>, <code>suspended</code>.
					Suspending an operator immediately blocks all API access.
				</Param>
				<Param name="webhookUrl" type="string">
					Updated webhook URL.
				</Param>
				<Param name="allowedCurrencies" type="string[]">
					Updated list of allowed currencies.
				</Param>
				<Param name="allowedGames" type="string[]">
					Updated list of allowed games.
				</Param>
				<Param name="revenueShare" type="number">
					Updated revenue share percentage.
				</Param>
				<Param name="metadata" type="object">
					Updated custom metadata.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X PUT https://api.rngfort.com/api/v1/admin/operators/op_abc123 \\
  -H "Authorization: Bearer <adminToken>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Lucky Casino International",
    "allowedGames": ["dice", "plinko", "coinflip", "crash"],
    "revenueShare": 12
  }'`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "operator": {
      "id": "op_abc123",
      "name": "Lucky Casino International",
      "email": "admin@luckycasino.com",
      "status": "active",
      "walletMode": "seamless",
      "webhookUrl": "https://luckycasino.com/api/wallet",
      "allowedCurrencies": ["USD", "EUR", "BTC", "ETH"],
      "allowedGames": ["dice", "plinko", "coinflip", "crash"],
      "revenueShare": 12,
      "updatedAt": "2024-01-15T15:30:00Z"
    }
  }
}`}
			</CodeBlock>

			{/* Dashboard Stats */}
			<h2>Dashboard</h2>
			<p>
				Platform-wide statistics and metrics for monitoring overall performance.
			</p>

			{/* Get Dashboard Stats */}
			<h3 id="dashboard-stats">Dashboard Stats</h3>
			<Endpoint
				method="GET"
				path="/api/v1/admin/dashboard"
				description="Retrieves platform-wide statistics including total GGR, player counts, operator counts, and recent activity."
			/>

			<h4>Query Parameters</h4>
			<ParamList>
				<Param name="period" type="string">
					Time period: <code>today</code>, <code>yesterday</code>,{' '}
					<code>week</code>, <code>month</code>, <code>year</code>,{' '}
					<code>all</code>. Default: <code>today</code>.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X GET "https://api.rngfort.com/api/v1/admin/dashboard?period=week" \\
  -H "Authorization: Bearer <adminToken>"`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "summary": {
      "totalGgr": "761700",
      "totalBets": 152340,
      "totalWagered": "15234000",
      "activePlayers": 1542,
      "activeOperators": 12,
      "activeSessions": 89,
      "currency": "USD"
    },
    "comparison": {
      "ggrChange": 12.5,
      "betsChange": 8.3,
      "playersChange": 15.2
    },
    "topOperators": [
      {
        "id": "op_abc123",
        "name": "Lucky Casino",
        "ggr": "156000",
        "bets": 32000
      },
      {
        "id": "op_def456",
        "name": "Crypto Bet",
        "ggr": "142000",
        "bets": 28000
      }
    ],
    "topGames": [
      { "game": "dice", "ggr": "380850", "bets": 76170 },
      { "game": "plinko", "ggr": "228510", "bets": 45702 },
      { "game": "coinflip", "ggr": "114255", "bets": 22851 },
      { "game": "crash", "ggr": "38085", "bets": 7617 }
    ],
    "recentActivity": [
      {
        "type": "big_win",
        "playerId": "player_xxx",
        "operatorId": "op_abc123",
        "amount": "50000",
        "multiplier": 100,
        "game": "plinko",
        "timestamp": "2024-01-15T14:25:00Z"
      }
    ]
  }
}`}
			</CodeBlock>

			{/* Transactions */}
			<h2>Transactions</h2>
			<p>
				View and filter all transactions across the platform for auditing and
				monitoring.
			</p>

			{/* List All Transactions */}
			<h3 id="admin-transactions">All Transactions</h3>
			<Endpoint
				method="GET"
				path="/api/v1/admin/transactions"
				description="Retrieves a paginated list of all transactions across all operators with advanced filtering options."
			/>

			<h4>Query Parameters</h4>
			<ParamList>
				<Param name="operatorId" type="string">
					Filter by operator ID.
				</Param>
				<Param name="playerId" type="string">
					Filter by player ID.
				</Param>
				<Param name="type" type="string">
					Transaction type: <code>bet</code>, <code>win</code>,{' '}
					<code>deposit</code>, <code>withdrawal</code>, <code>refund</code>.
				</Param>
				<Param name="game" type="string">
					Filter by game.
				</Param>
				<Param name="currency" type="string">
					Filter by currency.
				</Param>
				<Param name="minAmount" type="string">
					Minimum transaction amount.
				</Param>
				<Param name="maxAmount" type="string">
					Maximum transaction amount.
				</Param>
				<Param name="startDate" type="string">
					Start date (ISO 8601).
				</Param>
				<Param name="endDate" type="string">
					End date (ISO 8601).
				</Param>
				<Param name="limit" type="number">
					Results per page (1-100, default: 50).
				</Param>
				<Param name="cursor" type="string">
					Pagination cursor.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X GET "https://api.rngfort.com/api/v1/admin/transactions?type=bet&minAmount=10000&limit=20" \\
  -H "Authorization: Bearer <adminToken>"`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "transactions": [
      {
        "transactionId": "tx_001",
        "type": "bet",
        "operatorId": "op_abc123",
        "operatorName": "Lucky Casino",
        "playerId": "player_xyz",
        "sessionId": "sess_abc",
        "gameId": "dice",
        "betId": "bet_001",
        "amount": "15000",
        "currency": "USD",
        "createdAt": "2024-01-15T14:30:00Z"
      },
      {
        "transactionId": "tx_002",
        "type": "win",
        "operatorId": "op_abc123",
        "operatorName": "Lucky Casino",
        "playerId": "player_xyz",
        "sessionId": "sess_abc",
        "gameId": "dice",
        "betId": "bet_001",
        "amount": "29700",
        "currency": "USD",
        "createdAt": "2024-01-15T14:30:01Z"
      }
    ],
    "pagination": {
      "hasMore": true,
      "cursor": "eyJsYXN0SWQiOiJ0eF8wMDIifQ==",
      "total": 1523
    },
    "summary": {
      "totalAmount": "44700",
      "count": 2
    }
  }
}`}
			</CodeBlock>

			{/* Analytics */}
			<h2>Analytics</h2>
			<p>
				Advanced analytics and reporting for business intelligence and trend
				analysis.
			</p>

			{/* Get Analytics */}
			<h3 id="analytics">Analytics Data</h3>
			<Endpoint
				method="GET"
				path="/api/v1/admin/analytics"
				description="Retrieves detailed analytics data with time series, breakdowns, and trend analysis."
			/>

			<h4>Query Parameters</h4>
			<ParamList>
				<Param name="metric" type="string" required>
					Metric to analyze: <code>ggr</code>, <code>bets</code>,{' '}
					<code>players</code>, <code>sessions</code>, <code>wagered</code>.
				</Param>
				<Param name="startDate" type="string" required>
					Start date for analysis (ISO 8601).
				</Param>
				<Param name="endDate" type="string" required>
					End date for analysis (ISO 8601).
				</Param>
				<Param name="granularity" type="string">
					Time granularity: <code>hour</code>, <code>day</code>,{' '}
					<code>week</code>, <code>month</code>. Default: <code>day</code>.
				</Param>
				<Param name="groupBy" type="string">
					Additional grouping: <code>operator</code>, <code>game</code>,{' '}
					<code>currency</code>.
				</Param>
				<Param name="operatorId" type="string">
					Filter to specific operator.
				</Param>
			</ParamList>

			<h4>Example Request</h4>
			<CodeBlock language="bash" title="cURL">
				{`curl -X GET "https://api.rngfort.com/api/v1/admin/analytics?metric=ggr&startDate=2024-01-01&endDate=2024-01-15&granularity=day&groupBy=game" \\
  -H "Authorization: Bearer <adminToken>"`}
			</CodeBlock>

			<h4>Example Response</h4>
			<CodeBlock language="json" title="200 OK">
				{`{
  "success": true,
  "data": {
    "metric": "ggr",
    "period": {
      "start": "2024-01-01T00:00:00Z",
      "end": "2024-01-15T23:59:59Z"
    },
    "summary": {
      "total": "1142550",
      "average": "76170",
      "min": "45000",
      "max": "125000",
      "trend": 8.5
    },
    "timeSeries": [
      {
        "date": "2024-01-15",
        "total": "76170",
        "byGame": {
          "dice": "38085",
          "plinko": "22851",
          "coinflip": "11426",
          "crash": "3808"
        }
      },
      {
        "date": "2024-01-14",
        "total": "82500",
        "byGame": {
          "dice": "41250",
          "plinko": "24750",
          "coinflip": "12375",
          "crash": "4125"
        }
      }
    ],
    "breakdown": {
      "byGame": [
        { "game": "dice", "value": "571275", "percentage": 50 },
        { "game": "plinko", "value": "342765", "percentage": 30 },
        { "game": "coinflip", "value": "171383", "percentage": 15 },
        { "game": "crash", "value": "57127", "percentage": 5 }
      ]
    }
  }
}`}
			</CodeBlock>

			{/* Admin Roles */}
			<h2>Admin Roles & Permissions</h2>
			<Table
				columns={[
					{ key: 'role', header: 'Role' },
					{ key: 'operators', header: 'Operators' },
					{ key: 'transactions', header: 'Transactions' },
					{ key: 'analytics', header: 'Analytics' },
					{ key: 'settings', header: 'Settings' },
				]}
				data={[
					{
						role: 'Super Admin',
						operators: 'Full Access',
						transactions: 'Full Access',
						analytics: 'Full Access',
						settings: 'Full Access',
					},
					{
						role: 'Admin',
						operators: 'Read/Write',
						transactions: 'Read Only',
						analytics: 'Full Access',
						settings: 'Limited',
					},
					{
						role: 'Analyst',
						operators: 'Read Only',
						transactions: 'Read Only',
						analytics: 'Full Access',
						settings: 'None',
					},
					{
						role: 'Support',
						operators: 'Read Only',
						transactions: 'Read Only',
						analytics: 'None',
						settings: 'None',
					},
				]}
			/>

			{/* Rate Limits */}
			<h2>Rate Limits</h2>
			<CodeBlock language="json" title="Admin API Rate Limits">
				{`{
  "admin_api": {
    "requests_per_minute": 500,
    "requests_per_hour": 10000,
    "analytics_queries_per_minute": 30
  }
}`}
			</CodeBlock>

			{/* Next Steps */}
			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>
				<ul>
					<li>
						<a href="/docs/api-operators">Operator API</a> - API for operator
						integrations
					</li>
					<li>
						<a href="/docs/webhooks">Webhooks</a> - Configure event
						notifications
					</li>
					<li>
						<a href="/docs/errors">Error Codes</a> - Handle API errors
					</li>
				</ul>
			</div>
		</div>
	);
}
